import { useEffect, useState } from 'react';
import styles from './OfflineBanner.module.css';

/**
 * A banner that announces the offline state via an aria-live region.
 *
 * It is driven by the browser's online/offline events. When the browser
 * reports `navigator.onLine === true` the banner is hidden. It must NOT
 * claim any pending action succeeded — it only reports connectivity.
 */
export function OfflineBanner() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    function handleOffline() {
      setIsOffline(true);
    }
    function handleOnline() {
      setIsOffline(false);
    }

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  if (!isOffline) {
    return null;
  }

  return (
    <div className={styles.banner} role="status" aria-live="polite">
      <span className={styles.icon} aria-hidden="true">
        {/* A simple signal-slash icon drawn as inline SVG for zero deps. */}
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <line x1="1" y1="1" x2="23" y2="23" />
          <path d="M10.7 5C12.15 5 13.58 5.43 14.83 6.18" />
          <path d="M15.15 9.15A7.85 7.85 0 0 1 17 14" />
          <path d="M15.44 15.44A7.85 7.85 0 0 1 13 19.94" />
          <path d="M9.9 21.88a7.91 7.91 0 0 1-3.2-1.57" />
          <path d="M1 1l22 22" />
        </svg>
      </span>
      <span className={styles.message}>
        You are offline. The network is not available. Your work has not been lost.
      </span>
    </div>
  );
}
